<template>
  <div class="vendor-dashboard">
    <!-- Key Metrics -->
    <div class="md-layout md-gutter">
      <div class="md-layout-item md-size-25 md-medium-size-50 md-small-size-100">
        <md-card class="md-card-stats">
          <md-card-header class="md-card-header-icon md-card-header-primary">
            <div class="card-icon">
              <md-icon>assignment</md-icon>
            </div>
            <p class="card-category">Active Guidances</p>
            <h3 class="card-title">{{ formatNumber(activeGuidances) }}</h3>
          </md-card-header>
          <md-card-content>
            <div class="md-card-stats">
              <span class="text-success">
                <md-icon>arrow_upward</md-icon>
                {{ formatNumber(Math.round(activeGuidances * 0.12)) }}%
              </span>
              <span class="text-muted">Since last month</span>
            </div>
          </md-card-content>
        </md-card>
      </div>

      <div class="md-layout-item md-size-25 md-medium-size-50 md-small-size-100">
        <md-card class="md-card-stats">
          <md-card-header class="md-card-header-icon md-card-header-success">
            <div class="card-icon">
              <md-icon>people</md-icon>
            </div>
            <p class="card-category">Active Labourers</p>
            <h3 class="card-title">{{ formatNumber(activeLabourers) }}</h3>
          </md-card-header>
          <md-card-content>
            <div class="md-card-stats">
              <span class="text-success">
                <md-icon>arrow_upward</md-icon>
                {{ formatNumber(Math.round(activeLabourers * 0.08)) }}%
              </span>
              <span class="text-muted">Since last month</span>
            </div>
          </md-card-content>
        </md-card>
      </div>

      <div class="md-layout-item md-size-25 md-medium-size-50 md-small-size-100">
        <md-card class="md-card-stats">
          <md-card-header class="md-card-header-icon md-card-header-warning">
            <div class="card-icon">
              <md-icon>attach_money</md-icon>
            </div>
            <p class="card-category">Total Revenue</p>
            <h3 class="card-title">{{ formatCurrency(totalRevenue) }}</h3>
          </md-card-header>
          <md-card-content>
            <div class="md-card-stats">
              <span class="text-success">
                <md-icon>arrow_upward</md-icon>
                {{ formatNumber(Math.round(totalRevenue * 0.15)) }}%
              </span>
              <span class="text-muted">Since last month</span>
            </div>
          </md-card-content>
        </md-card>
      </div>

      <div class="md-layout-item md-size-25 md-medium-size-50 md-small-size-100">
        <md-card class="md-card-stats">
          <md-card-header class="md-card-header-icon md-card-header-info">
            <div class="card-icon">
              <md-icon>check_circle</md-icon>
            </div>
            <p class="card-category">Completed Assignments</p>
            <h3 class="card-title">{{ formatNumber(completedAssignments) }}</h3>
          </md-card-header>
          <md-card-content>
            <div class="md-card-stats">
              <span class="text-success">
                <md-icon>arrow_upward</md-icon>
                {{ formatNumber(Math.round(completedAssignments * 0.05)) }}%
              </span>
              <span class="text-muted">Since last month</span>
            </div>
          </md-card-content>
        </md-card>
      </div>
    </div>

    <!-- Charts Row -->
    <div class="md-layout md-gutter">
      <div class="md-layout-item md-size-50 md-medium-size-100 md-small-size-100">
        <md-card>
          <md-card-header>
            <h4 class="title">
              <md-icon>people</md-icon>
              Labourer Utilization
            </h4>
            <p class="category">Distribution of labourers by status</p>
          </md-card-header>
          <md-card-content>
            <div class="chart-container">
              <canvas ref="utilizationChart"></canvas>
            </div>
          </md-card-content>
          <md-card-actions>
            <md-button class="md-primary">View Details</md-button>
          </md-card-actions>
        </md-card>
      </div>

      <div class="md-layout-item md-size-50 md-medium-size-100 md-small-size-100">
        <md-card>
          <md-card-header>
            <h4 class="title">
              <md-icon>trending_up</md-icon>
              Revenue Trend
            </h4>
            <p class="category">Monthly revenue performance</p>
          </md-card-header>
          <md-card-content>
            <div class="chart-container">
              <canvas ref="revenueChart"></canvas>
            </div>
          </md-card-content>
          <md-card-actions>
            <md-button class="md-primary">Export Report</md-button>
          </md-card-actions>
        </md-card>
      </div>
    </div>

    <!-- Table -->
    <md-card>
      <md-card-header class="md-card-header-icon md-card-header-success">
        <div class="card-icon">
          <md-icon>assignment</md-icon>
        </div>
        <h4 class="title">Last Guidances</h4>
        <p class="category">List of active guidances</p>
      </md-card-header>
      <md-card-content>
        <md-table>
          <md-table-row>
            <md-table-head>Guidance #</md-table-head>
            <md-table-head>Department</md-table-head>
            <md-table-head>Start Date</md-table-head>
            <md-table-head>End Date</md-table-head>
            <md-table-head class="text-right">Labourers</md-table-head>
            <md-table-head>Status</md-table-head>
            <md-table-head class="text-center">Actions</md-table-head>
          </md-table-row>

          <md-table-row v-for="guidance in filteredGuidances" :key="guidance.id">
            <md-table-cell>
              <strong class="guidance-link" @click="viewGuidance(guidance.id)">
                {{ guidance.guidance_number }}
              </strong>
            </md-table-cell>
            <md-table-cell>{{ guidance.department_name }}</md-table-cell>
            <md-table-cell>{{ formatDate(guidance.start_date) }}</md-table-cell>
            <md-table-cell>{{ formatDate(guidance.end_date) }}</md-table-cell>
            <md-table-cell class="text-right">
              <span class="labourer-count">
                <md-icon>people</md-icon>
                {{ guidance.labourer_count }}
              </span>
            </md-table-cell>
            <md-table-cell>
              <badge :type="getStatusBadge(guidance.status)">{{ guidance.status }}</badge>
            </md-table-cell>
            <md-table-cell class="text-center">
              <md-button class="md-icon-button md-primary" @click="viewGuidance(guidance.id)" title="View">
                <md-icon>visibility</md-icon>
              </md-button>
            </md-table-cell>
          </md-table-row>

          <tr v-if="filteredGuidances.length === 0">
            <td colspan="7" class="text-center empty-state">
              <md-icon>inbox</md-icon>
              <p>No active guidances found</p>
            </td>
          </tr>
        </md-table>

        <div class="md-layout md-gutter md-alignment-center">
          <div class="md-layout-item md-size-50">
            <p class="card-category text-center">
              Showing {{ filteredGuidances.length }} of {{ activeGuidancesList.length }} entries
            </p>
          </div>
        </div>
      </md-card-content>
      <md-card-actions>
        <md-button class="md-primary" @click="viewAllGuidances">View All</md-button>
        <md-button class="md-info" @click="refreshDashboard">
          <md-icon>refresh</md-icon>
          Refresh
        </md-button>
      </md-card-actions>
    </md-card>
  </div>
</template>

<script>
import Badge from '@/components/Badge.vue';
import { mapGetters } from 'vuex';
import Chart from 'chart.js/auto';

export default {
  name: 'VendorDashboard',
  components: {
    Badge: Badge
  },
  props: {
    dateRange: {
      type: Object,
      default: function() {
        return {};
      }
    }
  },
  data: function() {
    return {
      activeGuidances: 0,
      activeLabourers: 0,
      totalRevenue: 0,
      completedAssignments: 0,
      activeGuidancesList: [],
      recentGuidancesList: [],
      revenueValue: [],
      revenueLabel: [],
      searchQuery: '',
      labourerUtilization: [],
      utilizationChart: null,
      revenueChart: null
    };
  },
  computed: {
    ...mapGetters('dashboard', ['isLoading', 'vendorStats']),
    me: function() {
      return this.$store.state.profile && this.$store.state.profile.me ? this.$store.state.profile.me : null;
    },
    filteredGuidances: function() {
      var self = this;
      if (!this.searchQuery) {
        return this.activeGuidancesList;
      }
      var query = this.searchQuery.toLowerCase();
      return this.activeGuidancesList.filter(function(item) {
        var guidanceNumber = item.guidance_number ? item.guidance_number.toLowerCase() : '';
        var departmentName = item.department_name ? item.department_name.toLowerCase() : '';
        var status = item.status ? item.status.toLowerCase() : '';
        return guidanceNumber.includes(query) ||
            departmentName.includes(query) ||
            status.includes(query);
      });
    }
  },
  created: function() {
    this.fetchDashboardData();
  },
  mounted: function() {
    var self = this;
    this.$nextTick(function() {
      self.initCharts();
    });
  },
  beforeDestroy: function() {
    if (this.utilizationChart) {
      this.utilizationChart.destroy();
    }
    if (this.revenueChart) {
      this.revenueChart.destroy();
    }
  },
  watch: {
    me: {
      handler: function (val) {
        if (!val) return;
        var targetId = (val.model && val.model.department) ? val.model.department.id : null;
        if (targetId && targetId !== this.departmentId) {
          this.departmentId = targetId;
          this.fetchDashboardData();
        }
      },
      deep: true,
      immediate: true
    },
    dateRange: {
      handler: function () {
        this.fetchDashboardData();
      },
      deep: true
    }
  },
  methods: {
    async fetchDashboardData() {
      try {
        var params = {
          date_range: this.dateRange
        };

        await this.$store.dispatch('dashboard/fetchVendorStats', params);
        var data = this.vendorStats || {};

        this.activeGuidances = data.active_guidances || 0;
        this.activeLabourers = data.active_labourers || 0;
        this.totalRevenue = data.total_revenue || 0;
        this.completedAssignments = data.completed_assignments || 0;
        this.activeGuidancesList = data.guidances || [];
        this.revenueValue = data.trend_revenue || [];
        this.revenueLabel = data.trend_labels || [];
        this.labourerUtilization = data.utilization || [];

        var self = this;
        this.$nextTick(function() {
          self.initCharts();
        });
      } catch (error) {
        console.error('Error fetching vendor dashboard:', error);
        this.$store.dispatch('alerts/error', 'Failed to load dashboard data');
      }
    },

    initCharts: function() {
      this.initUtilizationChart();
      this.initRevenueChart();
    },

    initUtilizationChart: function() {
      var ctx = this.$refs.utilizationChart ? this.$refs.utilizationChart.getContext('2d') : null;
      if (!ctx) return;

      if (this.utilizationChart) {
        this.utilizationChart.destroy();
      }

      this.utilizationChart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: Object.keys(this.labourerUtilization),
          datasets: [{
            data: Object.values(this.labourerUtilization),
            backgroundColor: ['#4caf50', '#ff9800', '#2196f3'],
            borderWidth: 0
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                usePointStyle: true,
                padding: 20
              }
            }
          },
          cutout: '70%'
        }
      });
    },

    initRevenueChart: function() {
      var ctx = this.$refs.revenueChart ? this.$refs.revenueChart.getContext('2d') : null;
      if (!ctx) return;

      if (this.revenueChart) {
        this.revenueChart.destroy();
      }

      var months = this.revenueLabel;
      var revenueData = this.revenueValue;

      this.revenueChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: months,
          datasets: [{
            label: 'Revenue',
            data: revenueData,
            borderColor: '#4caf50',
            backgroundColor: 'rgba(76, 175, 80, 0.1)',
            fill: true,
            tension: 0.4,
            pointBackgroundColor: '#4caf50',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 4
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                callback: function(value) {
                  return 'TZS ' + value.toLocaleString();
                }
              }
            }
          }
        }
      });
    },

    refreshDashboard: function() {
      this.fetchDashboardData();
      this.$store.dispatch('alerts/success', 'Dashboard refreshed');
    },

    viewGuidance: function(id) {
      this.$router.push({ name: 'View Guidance', params: { id: id } });
    },

    viewAllGuidances: function() {
      this.$router.push({ name: 'Guidances List' });
    },

    formatNumber: function(value) {
      if (!value) return '0';
      return new Intl.NumberFormat().format(value);
    },

    formatCurrency: function(value) {
      if (!value) return 'TZS 0';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'TZS',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    },

    formatDate: function(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString();
    },

    getStatusBadge: function(status) {
      var badges = {
        'Created': 'info',
        'Reviewed': 'warning',
        'Approved': 'success',
        'Accepted': 'success',
        'Rejected': 'danger',
        'Draft': 'rose',
        'Active': 'success',
        'Inactive': 'danger'
      };
      return badges[status] || 'primary';
    }
  }
};
</script>

<style scoped>
.vendor-dashboard {
  padding: 20px;
}

/* Material Card Stats */
.md-card-stats .card-icon {
  border-radius: 3px;
  background-color: #999;
  padding: 15px;
  margin-top: -20px;
  margin-right: 15px;
  float: left;
}

.md-card-stats .card-icon .md-icon {
  color: #fff;
  width: 36px;
  height: 36px;
  font-size: 36px;
}

.md-card-stats .card-category {
  margin: 0;
  color: #999;
  font-size: 14px;
  font-weight: 400;
}

.md-card-stats .card-title {
  margin: 3px 0 0;
  font-size: 30px;
  font-weight: 300;
  line-height: 1.4;
  color: #3c4858;
}

.md-card-stats .md-card-stats {
  margin-top: 10px;
  color: #999;
}

.md-card-stats .md-card-stats .text-success {
  color: #4caf50;
}

.md-card-stats .md-card-stats .text-success .md-icon {
  font-size: 16px;
  width: 16px;
  height: 16px;
  vertical-align: middle;
}

.md-card-stats .md-card-stats .text-muted {
  color: #999;
  margin-left: 5px;
}

.md-card-header-icon .card-icon {
  background: linear-gradient(60deg, #ab47bc, #7b1fa2);
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.14), 0 7px 10px -5px rgba(156, 39, 176, 0.4);
}

.md-card-header-icon .card-icon.md-card-header-primary {
  background: linear-gradient(60deg, #ab47bc, #7b1fa2);
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.14), 0 7px 10px -5px rgba(156, 39, 176, 0.4);
}

.md-card-header-icon .card-icon.md-card-header-success {
  background: linear-gradient(60deg, #66bb6a, #388e3c);
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.14), 0 7px 10px -5px rgba(76, 175, 80, 0.4);
}

.md-card-header-icon .card-icon.md-card-header-warning {
  background: linear-gradient(60deg, #ffa726, #f57c00);
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.14), 0 7px 10px -5px rgba(255, 152, 0, 0.4);
}

.md-card-header-icon .card-icon.md-card-header-info {
  background: linear-gradient(60deg, #26c6da, #00838f);
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.14), 0 7px 10px -5px rgba(0, 188, 212, 0.4);
}

/* Chart */
.chart-container {
  position: relative;
  height: 250px;
  padding: 10px;
}

/* Table */
.guidance-link {
  color: #9c27b0;
  cursor: pointer;
}

.guidance-link:hover {
  text-decoration: underline;
}

.labourer-count {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.labourer-count .md-icon {
  font-size: 16px;
  width: 16px;
  height: 16px;
  color: #999;
}

.text-right {
  text-align: right !important;
}

.text-center {
  text-align: center !important;
}

.empty-state {
  padding: 40px 20px;
  color: #999;
}

.empty-state .md-icon {
  font-size: 48px;
  width: 48px;
  height: 48px;
  color: #ddd;
  display: block;
  margin: 0 auto 10px;
}

.empty-state p {
  margin: 0;
}

.md-card-actions {
  padding: 8px 20px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

/* Responsive */
@media (max-width: 768px) {
  .vendor-dashboard {
    padding: 10px;
  }

  .md-card-stats .card-icon {
    padding: 12px;
  }

  .md-card-stats .card-icon .md-icon {
    width: 28px;
    height: 28px;
    font-size: 28px;
  }

  .md-card-stats .card-title {
    font-size: 24px;
  }

  .chart-container {
    height: 200px;
  }
}

@media (max-width: 480px) {
  .md-card-stats .card-icon {
    padding: 10px;
    margin-top: -15px;
  }

  .md-card-stats .card-icon .md-icon {
    width: 22px;
    height: 22px;
    font-size: 22px;
  }

  .md-card-stats .card-title {
    font-size: 20px;
  }

  .chart-container {
    height: 180px;
  }
}
</style>